// src/components/ResourceCard.js
import React, { useState } from 'react';
import { trackResourceAccess, formatFileSize, getFileIcon, isPreviewable } from '../services/resourceService';
import FileTypeIcon from './FileTypeIcon';

const ResourceCard = ({ resource, currentUser, onDelete, showDeleteButton = false }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle resource click based on type
  const handleResourceClick = async () => {
    // Track access if user is available
    if (currentUser) {
      try {
        await trackResourceAccess(resource.id, currentUser.uid);
      } catch (error) {
        console.warn('Failed to track resource access:', error);
      }
    }

    if (resource.resourceType === 'link') {
      window.open(resource.url || resource.fileUrl, '_blank');
    } else if (resource.fileUrl) {
      // If it's a previewable file type, show preview, otherwise download
      if (isPreviewable(resource.fileType)) {
        setPreviewOpen(true);
      } else {
        window.open(resource.fileUrl, '_blank');
      }
    }
  };

  // Close preview
  const closePreview = () => {
    setPreviewOpen(false);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${resource.title}"?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      if (onDelete) {
        await onDelete(resource.id);
      }
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource');
    } finally {
      setIsDeleting(false);
    }
  };

  // Render preview content
  const renderPreview = () => {
    if (!resource.fileUrl) return null;
    
    const type = (resource.fileType || '').toLowerCase();
    
    if (type.includes('pdf')) {
      return (
        <iframe 
          src={`${resource.fileUrl}#toolbar=0&navpanes=0`}
          title={resource.title}
          width="100%"
          height="500px"
          style={{ border: 'none' }}
        />
      );
    }
    
    if (type.includes('image')) {
      return (
        <img 
          src={resource.fileUrl} 
          alt={resource.title} 
          style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
        />
      );
    }
    
    if (type.includes('video')) {
      return (
        <video 
          src={resource.fileUrl} 
          controls 
          style={{ maxWidth: '100%', maxHeight: '500px' }}
        />
      );
    }
    
    if (type.includes('audio')) {
      return (
        <audio 
          src={resource.fileUrl} 
          controls 
          style={{ width: '100%' }}
        />
      );
    }
    
    return (
      <div style={{ textAlign: 'center', padding: '50px 20px' }}>
        <p>Preview not available for this file type.</p>
        <a 
          href={resource.fileUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="btn btn-primary"
          style={{ marginTop: '20px' }}
        >
          Download File
        </a>
      </div>
    );
  };

  return (
    <>
      <div 
        style={{ 
          background: 'white', 
          padding: '24px', 
          borderRadius: '16px', 
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #f3f4f6',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
        onClick={handleResourceClick}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
        }}
      >
        {/* Delete button */}
        {showDeleteButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            disabled={isDeleting}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'rgba(239, 68, 68, 0.9)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              zIndex: 10,
              opacity: isDeleting ? 0.5 : 1
            }}
            title="Delete resource"
          >
            {isDeleting ? '...' : '×'}
          </button>
        )}

        {/* Header with icon and title */}
        <div style={{ 
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '16px',
          gap: '16px'
        }}>
          <div style={{ 
            fontSize: '32px', 
            width: '56px',
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderRadius: '12px',
            flexShrink: 0
          }}>
            <FileTypeIcon type={getFileIcon(resource.resourceType, resource.fileType)} size={28} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '18px',
              fontWeight: '600',
              lineHeight: '1.4',
              wordBreak: 'break-word',
              color: '#1a1a1a'
            }}>
              {resource.title}
            </h4>
            <div style={{ 
              fontSize: '14px', 
              color: '#6b7280',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <span>{formatDate(resource.uploadedAt)}</span>
              {resource.fileSize > 0 && (
                <span>• {formatFileSize(resource.fileSize)}</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Description */}
        {resource.description && (
          <p style={{ 
            margin: '0 0 20px 0', 
            fontSize: '14px', 
            color: '#6b7280',
            lineHeight: '1.5',
            flex: 1
          }}>
            {resource.description.length > 120 
              ? `${resource.description.substring(0, 120)}...` 
              : resource.description
            }
          </p>
        )}
        
        {/* Footer with metadata */}
        <div style={{ 
          marginTop: 'auto',
          paddingTop: '16px',
          borderTop: '1px solid #f3f4f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>
            {resource.uploadedBy && `By ${resource.uploadedBy}`}
          </div>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              background: 'rgba(99, 102, 241, 0.1)',
              color: '#6366f1',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {resource.resourceType === 'link' ? 'Link' : 
               resource.resourceType === 'video' ? 'Video' : 'Document'}
            </span>
            <span style={{ 
              color: '#6366f1', 
              fontWeight: '600',
              fontSize: '14px'
            }}>
              {resource.resourceType === 'link' ? 'Open →' : 
               isPreviewable(resource.fileType) ? 'Preview →' : 'Download →'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Resource Preview Modal */}
      {previewOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={closePreview}
        >
          <div 
            style={{
              background: 'white',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '90vh',
              borderRadius: '16px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #f3f4f6'
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600' }}>
                  {resource.title}
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                  {resource.description}
                </p>
              </div>
              <button 
                onClick={closePreview}
                style={{ 
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Modal Content */}
            <div style={{ 
              padding: '24px', 
              overflow: 'auto', 
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {renderPreview()}
            </div>
            
            {/* Modal Footer */}
            <div style={{ 
              padding: '16px 24px', 
              borderTop: '1px solid #f3f4f6',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button 
                onClick={closePreview}
                className="btn btn-outline"
                style={{ padding: '10px 20px' }}
              >
                Close
              </button>
              <a 
                href={resource.fileUrl || resource.url}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ padding: '10px 20px' }}
                onClick={(e) => e.stopPropagation()}
              >
                {resource.resourceType === 'link' ? 'Open Link' : 'Download'}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResourceCard;